import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Scale, FileSearch, TrendingUp, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const VALUE_PROPS = [
  {
    icon: FileSearch,
    title: "QPA Validation",
    desc: "Forensic analysis of every Qualifying Payment Amount against geographic and specialty benchmarks before you file.",
  },
  {
    icon: Scale,
    title: "IDR Strategy",
    desc: "Federal-certified IDR entity selection, batching logic, and offer modeling tuned to your case mix.",
  },
  {
    icon: Shield,
    title: "Compliance Shield",
    desc: "Full HIPAA-aligned audit trail, deadline tracking, and NSA Open Negotiation documentation.",
  },
  {
    icon: TrendingUp,
    title: "Recovery Analytics",
    desc: "Real-time dashboards on payer behavior, win rates, dispute aging, and net recovery per claim.",
  },
];

const FAQS = [
  {
    q: "What is the No Surprises Act IDR process?",
    a: "The Federal Independent Dispute Resolution (IDR) process is a baseball-style arbitration created under the No Surprises Act. When a payer and out-of-network provider can't agree on a payment for emergency or covered ancillary services, either party can initiate IDR. A certified IDR entity selects one of the two offers based on the QPA and additional credible information.",
  },
  {
    q: "How is PRISM different from a billing service?",
    a: "PRISM is purpose-built for NSA IDR — not generic revenue cycle work. Our platform automates eligibility checks, QPA challenges, batching, and certified IDRE filings, while our consultants build the case strategy. We charge on results, not hours.",
  },
  {
    q: "What types of providers do you serve?",
    a: "Emergency medicine, anesthesiology, radiology, pathology, hospitalist groups, air ambulance, and any provider rendering out-of-network NSA-covered services. We work with private practices, hospital-affiliated groups, and MSOs.",
  },
  {
    q: "What information do I need to get started?",
    a: "A sample of denied or underpaid out-of-network EOBs, your current payer mix, and basic claims volume. We sign a BAA before reviewing any PHI.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-blue-100/50 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-12 fade-up">
              <div className="overline text-slate-500 flex items-center gap-3">
                <span className="h-px w-12 bg-slate-300" />
                Payment Resolution & IDR System Management
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light mt-6 text-slate-900 leading-[1.05]">
                Out-of-network claims, <br />
                <span className="text-[#0A2540]">resolved with precision.</span>
              </h1>
              <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
                PRISM combines proprietary IDR software with white-glove consulting to maximize
                recovery under the No Surprises Act. We do the dispute math so you can focus on
                patient care.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button
                  asChild
                  data-testid="hero-primary-cta"
                  className="rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white h-12 px-6 gap-1"
                >
                  <Link to="/contact">Request consultation <ArrowUpRight className="h-4 w-4" /></Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-secondary-cta"
                  className="rounded-md h-12 px-6 border-slate-300 text-slate-900 hover:bg-slate-50"
                >
                  <Link to="/products">Explore the platform</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">What PRISM does</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900">
                Built for the entire IDR lifecycle.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-base text-slate-600 leading-relaxed">
                From the moment a payer issues an underpayment to final IDRE determination,
                PRISM handles the legal mechanics, the math, and the documentation — without
                disrupting your revenue cycle workflow.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {VALUE_PROPS.map((v, i) => (
              <div
                key={v.title}
                data-testid={`value-prop-${i}`}
                className="bg-white p-8 hover:bg-slate-50 transition-colors group"
              >
                <v.icon className="h-7 w-7 text-[#0A2540] group-hover:text-[#2563EB] transition-colors" />
                <div className="font-display text-xl font-medium mt-6 text-slate-900">{v.title}</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE SPLIT */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-slate-500">The PRISM Platform</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900">
                Every dispute. Every dollar. One source of truth.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">
                Our case management software ingests EOBs, validates QPAs, runs eligibility
                screens, drafts Open Negotiation letters, and tracks each dispute through
                the IDRE process — all from one workspace.
              </p>
              <ul className="mt-8 space-y-3 max-w-lg">
                {[
                  "Automated NSA eligibility screening",
                  "QPA comparison vs. FAIR Health & geographic data",
                  "Open Negotiation & IDR initiation generators",
                  "IDRE batching & strategic offer modeling",
                  "Full audit trail & PHI-safe document vault",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="relative border border-slate-200 bg-white aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzg1NDU1Mjd8MA&ixlib=rb-4.1.0&q=85"
                  alt="PRISM analytics dashboard"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white border border-slate-200 p-4">
                  <div className="overline text-slate-500">Live Case Dashboard</div>
                  <div className="mt-1 font-display text-lg text-slate-900">Real-time IDR pipeline visibility</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ESTIMATOR REMOVED */}

      {/* TESTIMONIAL */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">Client outcome</div>
              <div className="mt-6 border-l-2 border-[#0A2540] pl-8">
                <p className="font-display text-2xl sm:text-3xl font-light text-slate-900 leading-snug">
                  "PRISM took our IDR backlog from chaos to a structured 92% win rate.
                  The white-glove team understands NSA math better than the payers do."
                </p>
                <div className="mt-6">
                  <div className="text-sm font-medium text-slate-900">Dr. Anesthesiologist, MD</div>
                  <div className="text-sm text-slate-500">Out-of-Network Provider Group</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <img
                src="https://images.pexels.com/photos/19438562/pexels-photo-19438562.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Healthcare professional"
                className="w-full h-[480px] object-cover border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline text-slate-500">FAQ</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900">
                The IDR process, demystified.
              </h2>
              <p className="mt-6 text-slate-600">Still have questions?</p>
              <Button
                asChild
                data-testid="faq-cta-button"
                variant="outline"
                className="mt-4 rounded-md border-slate-300 hover:bg-white"
              >
                <Link to="/contact">Talk to an expert <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible data-testid="faq-accordion">
                {FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-slate-300">
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display text-lg text-slate-900 text-left py-6 hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
